<script lang="ts" setup>
    import {
        defineProps,
        ref,
        onMounted,
        computed,
        onBeforeUnmount,
        toRaw,
    } from "vue"
    import { useRouter, onBeforeRouteLeave } from "vue-router"
    import { Alert } from "~/logic"

    const router = useRouter()

    const props = defineProps({
        resourceComponent: { type: [Object, Function] },
        store: { type: Function, required: true },
        route: { type: [Object, null], required: true },
        type: { type: String, required: true },
    })

    const isEditRoute = props.type === "edit"
    const storeInstance = props.store()

    const form = ref<any>()
    const valid = ref(false)
    const formReady = ref(false)
    const resourceRef = ref<any>()

    const initialSnapshot = ref("")

    onMounted(async () => {
        await storeInstance.ready
        await storeInstance.initForm(props.route?.params?.id)

        initialSnapshot.value = JSON.stringify(toRaw(storeInstance.formItem))
        formReady.value = true

        window.addEventListener("beforeunload", beforeUnloadGuard, {
            capture: true,
        })
    })

    onBeforeUnmount(() => {
        window.removeEventListener("beforeunload", beforeUnloadGuard, {
            capture: true,
        })
    })

    const isDirty = computed(() => {
        if (!formReady.value) return false
        return (
            initialSnapshot.value !==
            JSON.stringify(toRaw(storeInstance.formItem))
        )
    })

    function beforeUnloadGuard(e: BeforeUnloadEvent) {
        if (isDirty.value) {
            e.preventDefault()
            e.returnValue = ""
        }
    }

    const confirmOpen = ref(false)
    let pendingResolve: ((v: "cancel" | "leave" | "save") => void) | null = null

    function askConfirm(): Promise<"cancel" | "leave" | "save"> {
        confirmOpen.value = true
        return new Promise((resolve) => {
            pendingResolve = resolve
        })
    }

    function resolveConfirm(action: "cancel" | "leave" | "save") {
        confirmOpen.value = false
        pendingResolve?.(action)
        pendingResolve = null
    }

    // --- Actions
    async function createOrUpdate() {
        form.value?.validate?.()
        if (!valid.value) return

        let id: any = null
        if (isEditRoute) {
            id = await storeInstance.update()
            Alert.success(`Modification ${storeInstance.config.title} réussie`)
        } else {
            id = await storeInstance.create()
            Alert.success(`Création ${storeInstance.config.title} réussie`)
        }

        initialSnapshot.value = JSON.stringify(toRaw(storeInstance.formItem))

        if (resourceRef.value?.onAfterSubmit) {
            await resourceRef.value.onAfterSubmit(id)
        } else {
            await router.back()
        }
    }

    async function back() {
        if (!isDirty.value) {
            storeInstance.closeForm()
            return router.back()
        }

        const choice = await askConfirm()
        if (choice === "cancel") return
        if (choice === "save") return createOrUpdate()

        // "leave"
        storeInstance.closeForm()
        return router.back()
    }

    // // Garde de navigation interne (changement de route)
    // onBeforeRouteLeave(async () => {
    //     if (!isDirty.value) return true
    //
    //     const choice = await askConfirm()
    //     if (choice === "cancel") return false
    //     if (choice === "save") {
    //         await createOrUpdate()
    //         return true
    //     }
    //     // "leave"
    //     return true
    // })
</script>

<template>
    <v-container>
        <v-form ref="form" v-model="valid" :disabled="!formReady">
            <component
                :is="resourceComponent"
                ref="resourceRef"
                v-model="storeInstance.formItem"
            />
        </v-form>

        <v-bottom-navigation grow>
            <v-btn icon="mdi-arrow-left" @click="back" />
            <v-btn
                :disabled="!valid"
                icon="mdi-content-save-all"
                @click="createOrUpdate"
            />
        </v-bottom-navigation>

        <!-- Dialog confirmation -->
        <v-dialog v-model="confirmOpen" max-width="520">
            <v-card>
                <v-card-title>Modifications non enregistrées</v-card-title>
                <v-card-text>
                    Vous avez des changements non enregistrés. Que
                    souhaitez-vous faire ?
                </v-card-text>
                <v-card-actions class="justify-end">
                    <v-btn
                        color="error"
                        variant="text"
                        @click="resolveConfirm('leave')"
                    >
                        Quitter sans enregistrer
                    </v-btn>
                    <v-btn color="primary" @click="resolveConfirm('save')"
                        >Enregistrer</v-btn
                    >
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-container>
</template>
